import { Card, Col, Row, Select, Statistic, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAvailableYears } from '../../fetchHooks/useAvailableYears';
import { useYearStats } from '../../fetchHooks/useYearStats';
import 'chartjs-adapter-date-fns';
import '../../App.scss';
import { formatCurrency } from '../Month/dayUtils.ts';
import Navbar from '../Navbar/Navbar.tsx';
import { YearStatsGraph } from './YearStatsGraph.tsx';

const { Title } = Typography;

const Year = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const currentYear = yearParam ? Number.parseInt(yearParam) : new Date().getFullYear();
  const currentMonth = Number.parseInt(searchParams.get('month') ?? '');
  const { data: yearData } = useYearStats(currentYear);
  const { data: availableYearsData } = useAvailableYears();

  const handleYearChange = (newYear: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('year', newYear.toString());
    setSearchParams(newParams);
  };

  if (!!yearParam && !currentMonth) {
    console.log('Showing Year stats page');
    const availableYears = availableYearsData?.years || [];
    const hasMultipleYears = availableYears.length > 1;

    return (
      <div>
        <Navbar />
        <div className="app-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Title level={1} style={{ marginBottom: 0 }}>
              {t('salary.yearStatistics', { year: currentYear })}
            </Title>
            {hasMultipleYears && (
              <Select
                value={currentYear}
                onChange={handleYearChange}
                style={{ minWidth: 120 }}
                options={availableYears.map((y) => ({ label: y.toString(), value: y }))}
              />
            )}
          </div>

          {/* Key Statistics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('salary.totalEarnings')}
                  value={yearData?.earnedTotal || 0}
                  formatter={(value) => formatCurrency(Number(value), { rounded: true })}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('salary.totalHours')}
                  value={yearData?.workingHoursTotal || 0}
                  suffix={t('salary.hrs')}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title={t('salary.workingDays')}
                  value={yearData?.workingDaysTotal || 0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Secondary Statistics */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('salary.averageEarningsPerMonth')}
                  value={yearData?.earnedTotal ? yearData.earnedTotal / 12 : 0}
                  formatter={(value) => formatCurrency(Number(value), { rounded: true })}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('salary.averageHoursPerDay')}
                  value={
                    yearData?.workingHoursTotal && yearData?.workingDaysTotal
                      ? (yearData.workingHoursTotal / yearData.workingDaysTotal).toFixed(2)
                      : 0
                  }
                  suffix={t('salary.hrs')}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('salary.overtime')}
                  value={yearData?.overtimeHoursTotal || 0}
                  suffix={t('salary.hrs')}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('salary.flexitime')}
                  value={yearData?.flexitimeHoursTotal || 0}
                  suffix={t('salary.hrs')}
                  valueStyle={{ color: '#13c2c2' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Earnings Breakdown */}
          <Card style={{ marginBottom: 24 }}>
            <Title level={3} style={{ marginBottom: 16 }}>
              {t('salary.earningsBreakdown')}
            </Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Statistic
                  title={t('salary.earningsExMVA')}
                  value={yearData?.earnedTotal || 0}
                  formatter={(value) => formatCurrency(Number(value), { rounded: true })}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Statistic
                  title={t('salary.earningsInclMVA')}
                  value={yearData?.earnedTotal ? yearData.earnedTotal * 1.25 : 0}
                  formatter={(value) => formatCurrency(Number(value), { rounded: true })}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
            </Row>
          </Card>

          {/* Charts */}
          <Card style={{ marginBottom: 24 }}>
            <Title level={3} style={{ marginBottom: 16 }}>
              {t('salary.monthlyBreakdown')}
            </Title>
            <YearStatsGraph yearData={yearData} isPerMonth />
          </Card>

          <Card>
            <Title level={3} style={{ marginBottom: 16 }}>
              {t('salary.cumulativeEarnings')}
            </Title>
            <YearStatsGraph yearData={yearData} isAccumulated />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="app-container">
        <Card>
          <Title level={2}>{t('salary.noYearSelected')}</Title>
          <p>{t('salary.selectYearToView')}</p>
        </Card>
      </div>
    </div>
  );
};

export default Year;
